import { supabase } from '../integrations/supabase/client';
// Logo será carregado da URL pública

/**
 * Aplica watermark usando Canvas API nativo
 * Suporta PNG, JPG, JPEG, WEBP
 * Coloca a watermark no centro da imagem com tamanho proporcional
 */
export const applyWatermark = async (imageFile: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    console.log('🎨 Iniciando aplicação de watermark para:', imageFile.name);
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Não foi possível obter contexto do canvas'));
      return;
    }

    const img = new Image();
    const logo = new Image();
    
    let imageLoaded = false;
    let logoLoaded = false;
    
    const processWatermark = () => {
      if (!imageLoaded || !logoLoaded) return;
      
      try {
        // Define o tamanho do canvas baseado na imagem original
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Desenha a imagem original
        ctx.drawImage(img, 0, 0);
        
        // Calcula o tamanho da watermark (20% da largura da imagem, mínimo 100px, máximo 300px)
        const watermarkSize = Math.max(100, Math.min(300, img.width * 0.2));
        const logoAspectRatio = logo.width / logo.height;
        const logoWidth = watermarkSize;
        const logoHeight = watermarkSize / logoAspectRatio;
        
        // Posiciona a watermark no centro da imagem
        const x = (img.width - logoWidth) / 2;
        const y = (img.height - logoHeight) / 2;
        
        // Adiciona semi-transparência para a watermark (80% de opacidade)
        ctx.globalAlpha = 0.8;
        
        // Desenha a watermark
        ctx.drawImage(logo, x, y, logoWidth, logoHeight);
        
        // Restaura a opacidade
        ctx.globalAlpha = 1.0;
        
        // Converte o canvas para blob (JPEG com qualidade alta)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              console.log('✅ Watermark aplicada com sucesso');
              resolve(blob);
            } else {
              reject(new Error('Falha ao gerar imagem com watermark'));
            }
          },
          'image/jpeg',
          0.92 // Qualidade alta
        );
      } catch (error) {
        console.error('❌ Erro ao processar watermark:', error);
        reject(error);
      }
    };
    
    // Carrega a imagem original
    img.onload = () => {
      console.log('📷 Imagem carregada:', img.width, 'x', img.height);
      imageLoaded = true;
      processWatermark();
    };
    
    img.onerror = () => {
      reject(new Error('Falha ao carregar a imagem'));
    };
    
    // Carrega o logo da watermark
    logo.onload = () => {
      console.log('🦊 Logo carregado:', logo.width, 'x', logo.height);
      logoLoaded = true;
      processWatermark();
    };
    
    logo.onerror = () => {
      reject(new Error('Falha ao carregar o logo da watermark'));
    };
    
    // Define as fontes das imagens
    img.src = URL.createObjectURL(imageFile);
    logo.src = '/lovable-uploads/b4176acb-0bdc-4388-98a0-1efb99d946a2.png';
  });
};

/**
 * Faz upload da imagem com watermark para o Supabase Storage
 */
export const uploadImageWithWatermark = async (
  imageFile: File,
  path?: string
): Promise<string> => {
  try {
    console.log('🚀 Iniciando upload com watermark para:', imageFile.name);
    
    // Verifica se o usuário está autenticado
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      throw new Error('Usuário não autenticado. Faça login novamente.');
    }
    
    console.log('✅ Sessão autenticada, prosseguindo com upload');
    
    // Aplica a watermark
    const watermarkedBlob = await applyWatermark(imageFile);
    
    // Gera nome único para o arquivo usando UUID + timestamp
    const uuid = crypto.randomUUID();
    const timestamp = Date.now();
    const fileExtension = 'jpg';
    const fileName = path || `property_${uuid}_${timestamp}.${fileExtension}`;
    
    console.log('📤 Fazendo upload para Supabase Storage:', fileName);
    
    // Faz upload para o Supabase Storage com retry
    let uploadError: any = null;
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      attempts++;
      
      const { error } = await supabase.storage
        .from('property-images')
        .upload(fileName, watermarkedBlob, {
          contentType: 'image/jpeg',
          upsert: false // Não sobrescrever, evitar conflitos
        });
      
      if (!error) {
        uploadError = null;
        break;
      }
      
      uploadError = error;
      
      if (attempts < maxAttempts) {
        console.log(`⚠️ Tentativa ${attempts} falhou, tentando novamente...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
      }
    }
    
    if (uploadError) {
      console.error('❌ Erro no upload após', maxAttempts, 'tentativas:', uploadError);
      
      // Mensagens de erro específicas
      if (uploadError.message?.includes('policy')) {
        throw new Error('Sem permissão para fazer upload. Verifique se está logado.');
      } else if (uploadError.message?.includes('size')) {
        throw new Error('Arquivo muito grande. Máximo permitido: 5MB');
      } else if (uploadError.message?.includes('duplicate')) {
        throw new Error('Já existe um arquivo com este nome.');
      } else {
        throw new Error(`Erro ao fazer upload: ${uploadError.message}`);
      }
    }
    
    // Obtém a URL pública
    const { data: urlData } = supabase.storage
      .from('property-images')
      .getPublicUrl(fileName);
    
    console.log('✅ Upload concluído. URL:', urlData.publicUrl);
    
    return urlData.publicUrl;
  } catch (error: any) {
    console.error('❌ Erro no processo de upload com watermark:', error);
    throw error;
  }
};

/**
 * Converte diferentes formatos de imagem para um formato suportado
 */
export const processImageFile = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Se já é JPG ou PNG, retorna como está
    if (file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png') {
      resolve(file);
      return;
    }
    
    // Para WEBP e outros formatos, converte para JPG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      ctx?.drawImage(img, 0, 0);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const convertedFile = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(convertedFile);
          } else {
            reject(new Error('Falha na conversão da imagem'));
          }
        },
        'image/jpeg',
        0.92
      );
    };
    
    img.onerror = () => {
      reject(new Error('Falha ao carregar imagem para conversão'));
    };
    
    img.src = URL.createObjectURL(file);
  });
};